import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { IconCheck } from "@tabler/icons-react";
import { forwardRef, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export interface CheckboxProps
  extends Omit<React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>, "asChild"> {
  label?: ReactNode;
  description?: ReactNode;
}

/** Reemplazo de `<Checkbox>` de Mantine (Radix UI). */
export const Checkbox = forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, label, description, id, ...props }, ref) => {
    const autoId = id ?? props.name;
    const box = (
      <CheckboxPrimitive.Root
        ref={ref}
        id={autoId}
        className={cn(
          "flex size-4 shrink-0 items-center justify-center rounded-sm border border-input bg-background transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-multiva-600/30",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          "data-[state=checked]:bg-multiva-600 data-[state=checked]:border-multiva-600",
          className
        )}
        {...props}
      >
        <CheckboxPrimitive.Indicator className="text-white">
          <IconCheck size={11} strokeWidth={3} />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );
    if (!label && !description) return box;
    return (
      <div className="flex items-start gap-2">
        {box}
        <div className="flex flex-col gap-0.5">
          {label && (
            <label htmlFor={autoId} className="cursor-pointer text-sm text-foreground">
              {label}
            </label>
          )}
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
      </div>
    );
  }
);
Checkbox.displayName = "Checkbox";
